import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'
import travelDestinationObject from '@/entities/travelDestination/interface'

import {
  ALL_DESTINATIONS_BLOCKS_QUERY,
  SINGLE_DESTINATIONS_BLOCK_QUERY,
  CREATE_DESTINATIONS_BLOCK_MUTATION,
  UPDATE_DESTINATIONS_BLOCK_MUTATION,
  DELETE_DESTINATIONS_BLOCK_MUTATION,
  FORCE_DELETE_DESTINATIONS_BLOCK_MUTATION,
  ADD_DESTINATIONS_BLOCK_DESTINATIONS_MUTATION,
  REMOVE_DESTINATIONS_BLOCK_DESTINATIONS_MUTATION,
  CREATE_DESTINATIONS_BLOCK_DESTINATIONS_MUTATION,
  DELETE_DESTINATIONS_BLOCK_DESTINATIONS_MUTATION,
  UPDATE_DESTINATIONS_BLOCK_DESTINATIONS_ORDER_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const cmsDestinationsBlockRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_DESTINATIONS_BLOCK_QUERY, variables).then((data: any) => {
      return hydrateObject(data.destinationsBlock)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_DESTINATIONS_BLOCKS_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.destinationsBlocks)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_DESTINATIONS_BLOCK_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.destinationsBlock),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_DESTINATIONS_BLOCKS_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.destinationsBlocks),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_DESTINATIONS_BLOCK_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.destinationsBlock)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_DESTINATIONS_BLOCK_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.destinationsBlock)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_DESTINATIONS_BLOCK_MUTATION, variables).then((data: any) => {
      return data.deleteCmsDestinationsBlock
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_DESTINATIONS_BLOCK_MUTATION, variables).then((data: any) => {
      return data.forceDeleteCmsDestinationsBlock
    })
  },

  assignDestinations: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        destinationIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_DESTINATIONS_BLOCK_DESTINATIONS_MUTATION, variables)
  },

  unassignDestinations: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        destinationIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_DESTINATIONS_BLOCK_DESTINATIONS_MUTATION, variables)
  },

  createDestinations: async (id: string, items: Array<travelDestinationObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        destinations: items,
      },
    }
    return graphQLClient.request(CREATE_DESTINATIONS_BLOCK_DESTINATIONS_MUTATION, variables)
  },

  deleteDestinations: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        destinationIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_DESTINATIONS_BLOCK_DESTINATIONS_MUTATION, variables)
  },

  updateDestinationsOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        destinationIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_DESTINATIONS_BLOCK_DESTINATIONS_ORDER_MUTATION, variables)
  },
}

export default cmsDestinationsBlockRepository
